/**
 * Spese › Arrivate su FattureInCloud, da registrare (27/10/2026).
 *
 * La domanda: ci sono fatture dei fornitori che qui non vediamo?
 * Su FattureInCloud le fatture passive arrivano in Acquisti › Da registrare e restano lì finché
 * qualcuno non le registra (lo faceva Ylenia, dal 15/10 lo fa Lorenzo). Il gestionale importa solo
 * quelle registrate, quindi queste prima non si vedevano: il 26/10 erano dieci. L'API le legge dal
 * vivo (GET /admin/spese/da-registrare, tenute un quarto d'ora) e non le salva: registrate su
 * FattureInCloud, le porta dentro il sync come sempre, senza doppioni.
 *
 * La forma: sul computer come gli addebiti da spuntare, una riga che si apre; sul telefono un
 * gruppo come gli altri. Una riga per fattura: logo del fornitore, numero, giorno in cui è
 * arrivata, importo e il link alla pagina di FattureInCloud dove si registra. Nessun totale: quando
 * la fattura è registrata, il suo importo entra fra le spese da pagare, che hanno già il loro.
 * Se FattureInCloud non risponde lo dice una riga; se non è collegato, o non c'è niente da
 * registrare, la sezione non c'è.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { Icon } from '../../../components/ui/Icon';
import { BrandLogo } from '../../../components/ui/BrandLogo';
import { MGroup, MMore, leggibile } from '../../../components/mobile/MobileKit';
import { fmtDate, fmtEur } from '../../../lib/utils';
import { ilGiorno } from '../fatture/fattureApi';

export interface DocumentoDaRegistrare {
  /** Id del documento in attesa su FattureInCloud. */
  id: number;
  fornitore: string;
  numero: string | null;
  tipo: 'FATTURA' | 'NOTA_DI_CREDITO';
  /** Il giorno in cui è arrivata su FattureInCloud (YYYY-MM-DD). */
  arrivataIl: string | null;
  /** Lordo; negativo per le note di credito, come in Spese da pagare. */
  importo: number;
  /** Null se in euro; altrimenti il codice della valuta ("USD"). */
  valuta: string | null;
}

export interface DaRegistrare {
  /** OK = lista letta; NON_COLLEGATO = FattureInCloud non è collegato; NON_RAGGIUNGIBILE = non ha risposto. */
  stato: 'OK' | 'NON_COLLEGATO' | 'NON_RAGGIUNGIBILE';
  documenti: DocumentoDaRegistrare[];
  /** La pagina di FattureInCloud dove si registrano. */
  link: string;
}

export const daRegistrareQuery = {
  queryKey: ['admin', 'spese', 'da-registrare'] as const,
  queryFn: () => api.get<DaRegistrare>('/admin/spese/da-registrare'),
  // L'API tiene la lista un quarto d'ora: chiederla più spesso non porta niente di nuovo.
  staleTime: 5 * 60_000,
  retry: false,
};

/**
 * Cosa sono e cosa fare, in una frase: la stessa sul computer e sul telefono. "La mattina dopo"
 * perché il sync di FattureInCloud gira ogni mattina alle 5:30 (jobs/fic-sync.job.ts).
 */
const FRASE = 'Fatture dei fornitori arrivate su FattureInCloud che nessuno ha ancora registrato: finché restano lì non entrano fra le spese. Registrale su FattureInCloud e la mattina dopo arrivano qui da sole.';

const NON_RISPONDE = 'FattureInCloud adesso non risponde: le fatture arrivate lì e ancora da registrare non si possono vedere.';

const TITOLO_LINK = "Si apre FattureInCloud, in Acquisti › Da registrare: lì la trovi e la registri.";

/** "fattura n. 26037762/D", "nota di credito n. 12". */
const cosaE = (d: DocumentoDaRegistrare) => `${d.tipo === 'NOTA_DI_CREDITO' ? 'nota di credito' : 'fattura'}${d.numero ? ` n. ${d.numero}` : ''}`;

/** "arrivata il 13 ottobre". */
const arrivata = (d: DocumentoDaRegistrare) => (d.arrivataIl ? `arrivata ${ilGiorno(d.arrivataIl)}` : 'arrivata su FattureInCloud');

/** "€ 168,36", o "USD 20,00" se la fattura è in un'altra valuta: mai un euro che non è un euro. */
const importo = (d: DocumentoDaRegistrare) => `${d.valuta ?? '€'} ${fmtEur(d.importo, true)}`;

/** Il computer: una riga che si apre, sotto gli addebiti da spuntare. */
export function SpeseDaRegistrare() {
  const { data } = useQuery(daRegistrareQuery);
  const [aperti, setAperti] = useState(false);

  if (!data || data.stato === 'NON_COLLEGATO') return null;
  if (data.stato === 'NON_RAGGIUNGIBILE') {
    return <p className="ft-blocco sp-fic-ko"><Icon name="alert" size={13} /> {NON_RISPONDE}</p>;
  }
  if (!data.documenti.length) return null;

  return (
    <section className="ft-blocco sp-spunta" aria-label="Arrivate su FattureInCloud, da registrare">
      <button type="button" className="sp-spunta-hd" aria-expanded={aperti} onClick={() => setAperti((v) => !v)}>
        <span className="sp-spunta-n">{data.documenti.length}</span>
        <span className="min-w-0">
          <span className="t">Arrivate su FattureInCloud, da registrare</span>
          <span className="s">{FRASE}</span>
        </span>
        <Icon name={aperti ? 'chevronU' : 'chevronD'} size={13} className="mute" />
      </button>
      {aperti && (
        <div className="ft-lista sp-spunta-lista">
          {data.documenti.map((d) => (
            <div key={d.id} className="ft-riga sp-riga">
              <BrandLogo name={d.fornitore} size={32} />
              <span className="ft-chi">
                <span className="t" title={d.fornitore}>{leggibile(d.fornitore)}</span>
                <span className="s">{cosaE(d)}</span>
              </span>
              <span className="ft-quando" title={d.arrivataIl ? `Arrivata su FattureInCloud il ${fmtDate(d.arrivataIl)}` : undefined}>
                <span className="t">{arrivata(d)}</span>
              </span>
              <span className="ft-cifra">{importo(d)}</span>
              <span className="ft-azioni">
                <a className="btn btn-sm" href={data.link} target="_blank" rel="noopener noreferrer" title={TITOLO_LINK}>
                  <Icon name="link" size={10} /> Registra
                </a>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/** Quante righe mostra il gruppo del telefono prima di "Vedi tutte" (come gli altri gruppi di Spese). */
const PRIME = 6;

/** Il telefono: un gruppo come gli altri; toccando una riga si apre FattureInCloud. */
export function SpeseDaRegistrareMobile() {
  const { data } = useQuery(daRegistrareQuery);
  const [tutte, setTutte] = useState(false);

  if (!data || data.stato === 'NON_COLLEGATO') return null;
  if (data.stato === 'NON_RAGGIUNGIBILE') {
    return <p className="sp-fic-ko is-mobile"><Icon name="alert" size={13} /> {NON_RISPONDE}</p>;
  }
  if (!data.documenti.length) return null;

  const docs = data.documenti;
  const viste = tutte || docs.length <= PRIME + 1 ? docs : docs.slice(0, PRIME);
  return (
    <MGroup title="Arrivate su FattureInCloud, da registrare" count={docs.length} note={FRASE}>
      {viste.map((d) => (
        <a key={d.id} className="mk-row has-lead sp-fic-riga" href={data.link} target="_blank" rel="noopener noreferrer" title={TITOLO_LINK}>
          <BrandLogo name={d.fornitore} size={36} className="sp-fic-logo" />
          <span className="mk-main">
            <span className="mk-title">{leggibile(d.fornitore)}</span>
            <span className="mk-sub">{cosaE(d)} · {arrivata(d)}</span>
          </span>
          <span className="mk-trail">{importo(d)}</span>
          <Icon name="chevronR" size={13} className="mk-chev" />
        </a>
      ))}
      {docs.length > PRIME + 1 && (
        <MMore label={tutte ? 'Mostra meno' : `Vedi tutte le ${docs.length}`} onClick={() => setTutte((v) => !v)} />
      )}
    </MGroup>
  );
}
